import { Droplets, Sun, Thermometer, Wind, Activity } from "lucide-react";
import { useT } from "@/lib/i18n";
import { fmtP, impactOf } from "@/lib/pressureTargets";
import { SectionTitle } from "@/components/PressureInsights";

export const FACTORS = [
  { key: "track_temp", label: "track_temp", unit: "°C", step: 5, icon: Thermometer },
  { key: "air_temp", label: "air_temp", unit: "°C", step: 5, icon: Sun },
  { key: "humidity", label: "humidity_pct", unit: "%", step: 10, icon: Droplets },
  { key: "wind_speed", label: "wind_kmh", unit: "km/h", step: 10, icon: Wind },
];

const strength = (r) => { const a = Math.abs(r); return a >= 0.7 ? "strong" : a >= 0.4 ? "moderate" : a >= 0.2 ? "weak" : "none"; };
const S_CLS = { strong: "text-[#FF3B30]", moderate: "text-[#FFB800]", weak: "text-[#10B981]", none: "text-slate-500" };

const FactorCard = ({ f, imp, unit, active, onPick }) => {
  const { t } = useT();
  const Icon = f.icon;
  const ok = imp.slope != null;
  const s = ok ? strength(imp.r) : null;
  return (
    <button type="button" data-testid={`impact-${f.key}`} data-active={active ? "true" : "false"} onClick={() => onPick(f.key)}
      className={`text-left rounded-sm border p-4 transition-colors duration-200 ${active ? "border-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] bg-[#12141C] hover:border-slate-500"}`}>
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-400"><Icon className="w-3.5 h-3.5 text-[#FFB800]" />{t(f.label)}</div>
      {ok ? (
        <>
          <div data-testid={`impact-${f.key}-effect`} className="font-heading font-bold text-3xl leading-tight mt-1 tabular-nums">{fmtP(imp.slope * f.step, unit, true)}<span className="text-sm text-slate-500 ml-1">{unit}</span></div>
          <div className="font-mono text-[11px] text-slate-400">{t("impact_per").replace("{s}", `${f.step} ${f.unit}`)}</div>
          <div className="flex items-center justify-between gap-2 mt-2 font-mono text-[10px] uppercase">
            <span data-testid={`impact-${f.key}-strength`} className={S_CLS[s]}>{t(`impact_${s}`)} · r {imp.r.toFixed(2)}</span>
            <span className="text-slate-500">n={imp.n} · {Math.round(imp.min)}–{Math.round(imp.max)} {f.unit}</span>
          </div>
        </>
      ) : (
        <div data-testid={`impact-${f.key}-na`} className="font-mono text-xs text-slate-500 mt-2">{imp.flat ? t("impact_flat") : t("impact_few").replace("{n}", imp.n)}</div>
      )}
    </button>
  );
};

export const ConditionsImpact = ({ stints, unit, xKey, setXKey }) => {
  const { t } = useT();
  return (
    <section data-testid="conditions-impact">
      <SectionTitle icon={Activity}>{t("impact_title")}</SectionTitle>
      <p className="font-mono text-xs text-slate-500 -mt-1 mb-3">{t("impact_hint")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {FACTORS.map((f) => <FactorCard key={f.key} f={f} imp={impactOf(stints, f.key)} unit={unit} active={xKey === f.key} onPick={setXKey} />)}
      </div>
    </section>
  );
};
